// Reports row 4 (spec 044 D95–D97, Figma "Busiest hours" 11643:97142): the 14-day × 24-hour
// grid of the tutors who offer each hour and those booked in it, the switch between the two
// views, the four boxes and the footer.
import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Box, ButtonBase, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { ChartTooltip } from "../../components/kit/ChartTooltip";
import { focusRing, tokens } from "../../utils/figmaTokens";
import { hoursCard } from "../../utils/reportCharts";
import { cardSx, Head, NotReady } from "./Panels";

const LABEL = 140; // the day names' column, px

const BusiestHours = ({ state, hours, showSchedule, onRetry }) => {
  const [view, setView] = useState("booked");
  const card = useMemo(() => hoursCard(hours, view, new Date()), [hours, view]);
  const shown = state === "ready" && !hours ? "empty" : state;
  const { peakHour } = card;
  const outerRef = useRef(null);
  const scrollRef = useRef(null);
  const [tip, setTip] = useState(null);

  // D99: a phone shows about five hours; open the grid centred on the Peak hour, not on
  // midnight. A grid that fits does not scroll, so this does nothing on a wide screen. It runs
  // when the numbers load, not when the view switches, so a person who scrolled keeps their place.
  useLayoutEffect(() => {
    const box = scrollRef.current;
    if (!box || !hours || peakHour === null) return;
    const sq = box.querySelector(`[data-row="0"][data-hour="${peakHour}"]`);
    if (!sq) return;
    const b = box.getBoundingClientRect();
    const s = sq.getBoundingClientRect();
    box.scrollLeft += s.left + s.width / 2 - (b.left + LABEL + (b.width - LABEL) / 2);
  }, [hours, peakHour]);

  // The square under the pointer, or tapped: its tooltip sits outside the scroll box, which would
  // clip it — below a square near the top (the map's rule), to the left of one near the right.
  const pick = useCallback((e) => {
    const { row, hour } = e.target.dataset || {};
    if (row === undefined || hour === undefined) {
      // The 4 px gaps between squares are the grid itself: keep the tooltip over them.
      if (e.target !== e.currentTarget) setTip(null);
      return;
    }
    const box = outerRef.current.getBoundingClientRect();
    const sq = e.target.getBoundingClientRect();
    const top = sq.top - box.top;
    const next = {
      row: Number(row),
      hour: Number(hour),
      x: sq.left - box.left + sq.width / 2,
      y: top < 100 ? top + sq.height : top,
      below: top < 100,
      anchor: sq.left - box.left + sq.width / 2 > box.width - 100 ? "end" : "center",
    };
    setTip((t) => (t && t.row === next.row && t.hour === next.hour ? t : next));
  }, []);

  const grid = useMemo(
    () => (
      <Box
        role="img"
        aria-label={card.summary}
        onMouseMove={pick}
        onPointerUp={pick}
        onMouseLeave={() => setTip(null)}
        sx={{ display: "grid", gridTemplateColumns: `${LABEL}px repeat(24, minmax(28px, 1fr))`, columnGap: "4px", rowGap: "4px" }}
      >
        <Box />
        {card.hourLabels.map((label, h) => (
          <Box key={h} sx={{ height: 18 }}>
            <Typography
              component="span"
              sx={{ fontSize: 10, lineHeight: "14px", fontWeight: 500, letterSpacing: "0.3px", color: tokens.text.tertiary, whiteSpace: "nowrap" }}
            >
              {label}
            </Typography>
          </Box>
        ))}
        {card.rows.map((r, i) => (
          <React.Fragment key={r.key}>
            <Box
              sx={{
                height: 28,
                display: "flex",
                alignItems: "center",
                gap: "6px",
                position: "sticky",
                left: 0,
                zIndex: 1,
                bgcolor: tokens.surface.default,
                ...(r.gap ? { mt: "8px" } : {}),
              }}
            >
              <Typography
                component="span"
                sx={{
                  fontSize: 14,
                  lineHeight: "20px",
                  letterSpacing: "0.1px",
                  fontWeight: r.today ? 600 : 500,
                  color: r.today ? tokens.text.primary : tokens.text.secondary,
                }}
              >
                {r.label}
              </Typography>
              {r.today && (
                <Typography
                  component="span"
                  sx={{
                    bgcolor: tokens.chart.seq[0],
                    // MUI's blue: the brand blue in light, the dark link blue in dark, readable on
                    // the pill's dark fill (047 P134).
                    color: "primary.main",
                    px: "6px",
                    py: "1px",
                    borderRadius: "999px",
                    fontSize: 10,
                    lineHeight: "14px",
                    fontWeight: 600,
                    letterSpacing: "0.3px",
                  }}
                >
                  Today
                </Typography>
              )}
            </Box>
            {r.cells.map((c) => (
              <Box
                key={c.key}
                data-row={i}
                data-hour={c.key}
                sx={{
                  height: 28,
                  borderRadius: "6px",
                  bgcolor: c.level ? tokens.chart.seq[c.level - 1] : tokens.chart.seqEmpty,
                  position: "relative",
                  ...(r.gap ? { mt: "8px" } : {}),
                }}
              >
                {c.full && (
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      pointerEvents: "none",
                    }}
                  >
                    {/* D98: white is too faint on the two lightest steps. */}
                    <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: c.level <= 2 ? tokens.chart.seq[4] : tokens.text.inverse }} />
                  </Box>
                )}
              </Box>
            ))}
          </React.Fragment>
        ))}
      </Box>
    ),
    [card, pick]
  );

  const cell = tip && card.rows[tip.row] && card.rows[tip.row].cells[tip.hour];

  return (
    <Box component="section" aria-labelledby="reports-hours-title" sx={cardSx("24px", "20px")}>
      <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, alignItems: { xs: "stretch", md: "center" }, gap: "12px" }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Head id="reports-hours-title" title="Busiest hours" subtitle={card.subtitle} />
        </Box>
        {shown === "ready" && (
          <Box
            role="group"
            aria-label="View"
            sx={{ display: "flex", gap: "2px", p: "3px", bgcolor: tokens.surface.muted, borderRadius: "10px", flexShrink: 0, alignSelf: { xs: "flex-start", md: "auto" } }}
          >
            {[
              { key: "booked", label: "Booked sessions" },
              { key: "open", label: "Open slots" },
            ].map((t) => (
              <ButtonBase
                key={t.key}
                disableRipple
                aria-pressed={view === t.key}
                onClick={() => setView(t.key)}
                sx={{
                  px: "14px",
                  py: "6px",
                  borderRadius: "8px",
                  ...(view === t.key
                    ? { bgcolor: tokens.surface.default, boxShadow: tokens.shadow.raised, color: tokens.text.primary }
                    : { color: tokens.text.secondary }),
                  ...focusRing,
                }}
              >
                <Typography component="span" sx={{ fontSize: 14, lineHeight: "20px", fontWeight: 500, letterSpacing: "0.1px", color: "inherit" }}>
                  {t.label}
                </Typography>
              </ButtonBase>
            ))}
          </Box>
        )}
      </Box>

      {shown !== "ready" ? (
        <NotReady state={shown} height={474} onRetry={onRetry} />
      ) : (
        <>
          <Box sx={{ display: "grid", gap: "12px", gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" } }}>
            {card.stats.map((s) => (
              <Box
                key={s.key}
                sx={{
                  border: `1px solid ${tokens.border.subtle}`,
                  borderRadius: "12px",
                  px: "16px",
                  py: "12px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "2px",
                  minWidth: 0,
                }}
              >
                <Typography variant="labelMedium" sx={{ color: tokens.text.secondary }}>
                  {s.label}
                </Typography>
                <Typography variant="titleCard" sx={{ color: tokens.text.primary }}>
                  {s.value}
                </Typography>
                <Typography variant="bodySmall" sx={{ color: tokens.text.tertiary, minHeight: 22 }}>
                  {s.caption}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box ref={outerRef} sx={{ position: "relative" }}>
            <Box ref={scrollRef} onScroll={() => setTip(null)} sx={{ overflowX: "auto" }}>
              {grid}
            </Box>
            {cell && <ChartTooltip x={tip.x} y={tip.y} below={tip.below} anchor={tip.anchor} {...cell.tip} />}
          </Box>

          <Box sx={{ height: "1px", bgcolor: tokens.divider }} />

          <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", columnGap: "16px", rowGap: "8px" }}>
            <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "6px" }}>
              <Typography variant="bodySmall" sx={{ color: tokens.text.secondary, mr: "6px" }}>
                {card.legend.title}
              </Typography>
              {card.legend.keys.map((k) => (
                <Box key={k.level} sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  <Box
                    aria-hidden
                    sx={{ width: 16, height: 12, borderRadius: "3px", bgcolor: k.level ? tokens.chart.seq[k.level - 1] : tokens.chart.seqEmpty }}
                  />
                  <Typography variant="labelMedium" sx={{ color: tokens.text.secondary }}>
                    {k.label}
                  </Typography>
                </Box>
              ))}
              {card.legend.full && (
                <Box sx={{ display: "flex", alignItems: "center", gap: "4px", ml: "10px" }}>
                  <Box
                    aria-hidden
                    sx={{
                      width: 16,
                      height: 12,
                      borderRadius: "3px",
                      bgcolor: tokens.chart.seq[4],
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Box sx={{ width: 4, height: 4, borderRadius: "50%", bgcolor: tokens.text.inverse }} />
                  </Box>
                  <Typography variant="labelMedium" sx={{ color: tokens.text.secondary }}>
                    Fully booked
                  </Typography>
                </Box>
              )}
            </Box>
            <Box sx={{ flex: 1 }} />
            <Typography variant="bodySmall" sx={{ color: tokens.text.secondary }}>
              {card.summary}
            </Typography>
            {showSchedule && (
              <ButtonBase component={RouterLink} to="/lessons" sx={{ p: "8px", borderRadius: "8px", ...focusRing }}>
                <Typography variant="labelMedium" sx={{ color: tokens.text.link }}>
                  Open schedule
                </Typography>
              </ButtonBase>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

export default BusiestHours;
